import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Input from '@blaze-react/input';
import { components } from '@blaze-cms/react-page-builder';
import { CORE_TEXTBOOK_TEXT } from './constants';
import { inspectionCopyRequest, isFormValid } from './helpers';

const InspectionCopyForm = ({
  successMessage,
  inspectionCopyData,
  blazeAuthToken,
  blazeId,
  exceededPrintLimitMessage
}) => {
  const {
    einspection_copy_available: isEbookAvailable,
    print_inspection_copy_available: isPrintAvailable
  } = inspectionCopyData;

  const [formData, setFormData] = useState({
    nb_students: '1',
    inspection_copy_type: isEbookAvailable ? 'einspection_copy' : 'print_inspection_copy'
  });
  const [requestResponse, setRequestResponse] = useState({ loading: false });

  const handleInputChange = ({
    event: {
      target: { value, name }
    }
  }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = ({ target: { name, value } }) => {
    switch (name) {
      case 'bookshop_tagid':
        setFormData(prev => ({ ...prev, [name]: value, bookshop: '' }));
        break;
      case 'module_consist_of':
        setFormData(prev => ({ ...prev, [name]: value, current_adopted_textbook: '' }));
        break;

      default:
        setFormData(prev => ({ ...prev, [name]: value }));
        break;
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!requestResponse.loading && isFormValid(formData)) {
      setRequestResponse({ loading: true });
      inspectionCopyRequest(
        blazeId,
        blazeAuthToken,
        formData,
        setRequestResponse,
        exceededPrintLimitMessage
      );
    }
  };

  const { code: CodeComponent } = components;
  const { message, type } = requestResponse;
  const isCoreTextbook = formData.module_consist_of === CORE_TEXTBOOK_TEXT;
  const displayBookshopField = formData.bookshop_tagid === '0';
  const wasRequestSuccesful = type === 'success';
  const showErrorMessage = !!message && !wasRequestSuccesful;

  return (
    <div className="kp-inspection-copy-request__form-container">
      {wasRequestSuccesful ? (
        <div className="success-message">
          <CodeComponent code={successMessage} />
        </div>
      ) : (
        <form
          id="kp-inspection-copy-form"
          className="kp-inspection-copy-form"
          onSubmit={handleSubmit}>
          <div className="kp-inspection-copy-form-field">
            <Input
              name="module_names"
              error={formData.module_names === ''}
              value=""
              onChange={handleInputChange}
              label="Module name"
              modifier={formData.module_names === '' ? 'has-error' : ''}
              required
            />
          </div>
          <div className="kp-inspection-copy-form-field">
            <Input
              name="module_code"
              value=""
              onChange={handleInputChange}
              label="Module code"
              modifier={formData.module_code === '' ? 'has-error' : ''}
              error={formData.module_code === ''}
              required
            />
          </div>
          <div className="kp-inspection-copy-form-field">
            <Input
              type="number"
              name="nb_students"
              value={1}
              onChange={handleInputChange}
              label="Number of Students"
              modifier={formData.nb_students === '0' ? 'has-error' : ''}
              error={formData.nb_students === '0'}
              required
            />
          </div>

          <div className="kp-inspection-copy-form-field">
            <label htmlFor="academic_level" className="required">
              Academic level
            </label>
            <select name="academic_level" onChange={handleSelectChange} required>
              <option value="" disabled="">
                Please select academic level
              </option>
              <option value="L3 (UG foundation year)">L3 (UG foundation year)</option>
              <option value="L4 (Yr 1 UG)">L4 (Yr 1 UG)</option>
              <option value="L5 (Yr 2 UG)">L5 (Yr 2 UG)</option>
              <option value="L6 (Yr 3 or 4 UG)">L6 (Yr 3 or 4 UG)</option>
              <option value="L7 (MA or MSc)">L7 (MA or MSc)</option>
              <option value="MBA">MBA</option>
            </select>
          </div>

          <div className="kp-inspection-copy-form-field">
            <Input
              type="date"
              name="module_start_date"
              value=""
              onChange={handleInputChange}
              label="When does your Module begin next (exact or approximate start date)?"
              modifier={formData.module_start_date === '' ? 'has-error' : ''}
              error={formData.module_start_date === ''}
              required
            />
          </div>

          <div className="kp-inspection-copy-form-field">
            <label htmlFor="inspection_copy_type" className="required">
              Book Format
            </label>
            <select
              name="inspection_copy_type"
              onChange={handleSelectChange}
              value={formData.inspection_copy_type}
              required>
              <option value="" disabled>
                Please select book format
              </option>
              {isEbookAvailable && <option value="einspection_copy">Ebook</option>}
              {isPrintAvailable && <option value="print_inspection_copy">Print Copy</option>}
            </select>
          </div>

          <div className="kp-inspection-copy-form-field">
            <fieldset className="kp-inspection-copy-form-field__radio-field">
              <legend className="kp-inspection-copy-form-field__radio-field__label required">
                The Module reading list mostly contains:
              </legend>
              <div className="kp-inspection-copy-form-field__radio-field__radio-options">
                <div className="kp-inspection-copy-form-field__radio-field__radio-options__radio-option">
                  <input
                    type="radio"
                    name="module_consist_of"
                    value={CORE_TEXTBOOK_TEXT}
                    onChange={handleSelectChange}
                  />
                  <label htmlFor="module_consist_of">{CORE_TEXTBOOK_TEXT}</label>
                </div>
                <div className="kp-inspection-copy-form-field__radio-field__radio-options__radio-option">
                  <input
                    type="radio"
                    name="module_consist_of"
                    value="A wide reading list"
                    onChange={handleSelectChange}
                  />
                  <label htmlFor="module_consist_of">A wide reading list</label>
                </div>
                <div className="kp-inspection-copy-form-field__radio-field__radio-options__radio-option">
                  <input
                    type="radio"
                    name="module_consist_of"
                    value="Journal articles"
                    onChange={handleSelectChange}
                  />
                  <label htmlFor="module_consist_of">Journal articles</label>
                </div>
              </div>
            </fieldset>
          </div>

          {isCoreTextbook && (
            <div className="kp-inspection-copy-form-field">
              <Input
                name="current_adopted_textbook"
                value=""
                onChange={handleInputChange}
                label="Current adopted main textbook(s)"
                modifier={formData.current_adopted_textbook === '' ? 'has-error' : ''}
                error={formData.current_adopted_textbook === ''}
                required
              />
            </div>
          )}
          <div className="kp-inspection-copy-form-field">
            <label htmlFor="bookshop_tagid" className="required">
              How are core textbooks commonly accessed by students?
            </label>
            <select name="bookshop_tagid" onChange={handleSelectChange} required>
              <option value="" disabled="">
                Please select one option
              </option>
              <option value="482">
                Department buys books (print and/or ebook) for each student
              </option>
              <option value="483">Students access books via library</option>
              <option value="484">Students buy their own books</option>
              <option value="0">Other</option>
            </select>
          </div>
          {displayBookshopField && (
            <div className="kp-inspection-copy-form-field">
              <Input
                name="bookshop"
                value=""
                onChange={handleInputChange}
                label="Core textbooks access explained"
                modifier={formData.bookshop === '' ? 'has-error' : ''}
                error={formData.bookshop === ''}
                required
              />
            </div>
          )}
          {!wasRequestSuccesful && (
            <button type="submit" className="kp-inspection-copy-form__submit-button">
              Request inspection copy
            </button>
          )}
          {showErrorMessage && (
            <div className="error-message">
              <CodeComponent code={message} />
            </div>
          )}
          <div className="default-message">
            Your inspection copy will be an ebook, unless specified otherwise.
          </div>
        </form>
      )}
    </div>
  );
};

InspectionCopyForm.propTypes = {
  successMessage: PropTypes.string,
  blazeId: PropTypes.string.isRequired,
  blazeAuthToken: PropTypes.string.isRequired,
  exceededPrintLimitMessage: PropTypes.string,
  inspectionCopyData: PropTypes.object.isRequired
};

InspectionCopyForm.defaultProps = {
  successMessage: '',
  exceededPrintLimitMessage: ''
};

export default InspectionCopyForm;
